import styled from "@emotion/styled";
import { LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RadarIcon from "@mui/icons-material/Radar";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@avenue-8/sidekick-ui";

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.text.colors.brand};
`;

const CenteredColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const CountRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const Progress = styled(LinearProgress)`
  width: 100%;
  height: 0.5rem;
  border-radius: 999px;
  margin: 1.5rem 0 1rem;
`;

const ContactUsNote = styled(Typography)`
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid ${(p) => p.theme.colors.border2};
`;

type RadarConsumptionDialogProps = {
  open: boolean;
  onClose: () => void;
  count: number;
  maxContactsAllowed: number;
  remaining: number;
};

export const RadarConsumptionDialog = ({
  open,
  onClose,
  count,
  maxContactsAllowed,
  remaining,
}: RadarConsumptionDialogProps) => {
  const usedPercentage =
    maxContactsAllowed > 0
      ? Math.min(100, (count / maxContactsAllowed) * 100)
      : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="radar-consumption-title"
    >
      <DialogTitle id="radar-consumption-title">
        Radar monitoring
        <IconButton
          aria-label="Close dialog"
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <CenteredColumn>
          <IconWrap>
            <RadarIcon fontSize="medium" />
          </IconWrap>
          <CountRow>
            <Typography variant="h4">{count}</Typography>
            <Typography variant="h6" color="text.secondary">
              / {maxContactsAllowed}
            </Typography>
          </CountRow>
          <Typography variant="body2" color="text.secondary">
            contacts currently monitored by Radar
          </Typography>
          <Progress variant="determinate" value={usedPercentage} />
          <Typography variant="body2">
            {remaining} {remaining === 1 ? "slot" : "slots"} remaining on your
            current plan.
          </Typography>
          <ContactUsNote variant="body2" color="text.secondary">
            Need to monitor more contacts? Please contact support to discuss a
            custom plan.
          </ContactUsNote>
        </CenteredColumn>
      </DialogContent>
    </Dialog>
  );
};
